import { useState } from 'react';
import {
  Card, Button, Space, Table, Modal, Form, Input, InputNumber, Select, DatePicker, message, Popconfirm, Row, Col, Statistic, Tag
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { firmsApi } from '../../../api/firms';
import type { FirmFinancial, CreateFinancialRequest } from '../../../types';

function getTransactionTypeText(type: string) {
  switch (type) {
    case 'annual_fee': return 'Yıllık Ücret';
    case 'addon': return 'Ek Satış';
    case 'discount': return 'İndirim';
    case 'payment': return 'Ödeme';
    default: return type;
  }
}

function getTransactionTypeColor(type: string) {
  switch (type) {
    case 'annual_fee': return 'blue';
    case 'addon': return 'green';
    case 'discount': return 'orange';
    case 'payment': return 'purple';
    default: return 'default';
  }
}

export function FinancialSection({ firmId }: { firmId: number }) {
  const queryClient = useQueryClient();
  const [isFinancialModalOpen, setIsFinancialModalOpen] = useState(false);
  const [editingFinancial, setEditingFinancial] = useState<FirmFinancial | null>(null);
  const [financialForm] = Form.useForm();

  const { data: financials = [] } = useQuery({
    queryKey: ['firm-financials', firmId],
    queryFn: () => firmsApi.getFinancials(firmId),
    enabled: !!firmId,
  });

  const { data: financialSummary } = useQuery({
    queryKey: ['firm-financial-summary', firmId],
    queryFn: () => firmsApi.getFinancialSummary(firmId),
    enabled: !!firmId,
  });

  const addFinancialMutation = useMutation({
    mutationFn: (data: CreateFinancialRequest) => firmsApi.addFinancial(firmId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firm-financials', firmId] });
      queryClient.invalidateQueries({ queryKey: ['firm-financial-summary', firmId] });
      message.success('İşlem eklendi');
      setIsFinancialModalOpen(false);
      financialForm.resetFields();
    },
    onError: () => message.error('İşlem eklenemedi'),
  });

  const updateFinancialMutation = useMutation({
    mutationFn: ({ fid, data }: { fid: number; data: CreateFinancialRequest }) => firmsApi.updateFinancial(firmId, fid, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firm-financials', firmId] });
      queryClient.invalidateQueries({ queryKey: ['firm-financial-summary', firmId] });
      message.success('İşlem güncellendi');
      setIsFinancialModalOpen(false);
      setEditingFinancial(null);
      financialForm.resetFields();
    },
    onError: () => message.error('İşlem güncellenemedi'),
  });

  const deleteFinancialMutation = useMutation({
    mutationFn: (fid: number) => firmsApi.deleteFinancial(firmId, fid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firm-financials', firmId] });
      queryClient.invalidateQueries({ queryKey: ['firm-financial-summary', firmId] });
      message.success('İşlem silindi');
    },
    onError: () => message.error('İşlem silinemedi'),
  });

  const handleFinancialSubmit = (values: any) => {
    const data: CreateFinancialRequest = {
      transactionType: values.transactionType,
      description: values.description,
      amount: values.amount,
      currency: values.currency || 'TRY',
      periodStart: values.period?.[0]?.format('YYYY-MM-DD'),
      periodEnd: values.period?.[1]?.format('YYYY-MM-DD'),
      notes: values.notes,
    };

    if (editingFinancial) {
      updateFinancialMutation.mutate({ fid: editingFinancial.id, data });
    } else {
      addFinancialMutation.mutate(data);
    }
  };

  const financialColumns = [
    {
      title: 'Tarih',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY'),
    },
    {
      title: 'Tip',
      dataIndex: 'transactionType',
      key: 'transactionType',
      render: (type: string) => (
        <Tag color={getTransactionTypeColor(type)}>{getTransactionTypeText(type)}</Tag>
      ),
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Tutar',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number, record: FirmFinancial) => (
        <span style={{ color: record.transactionType === 'discount' ? 'red' : 'inherit' }}>
          {record.transactionType === 'discount' ? '-' : ''}
          {amount.toLocaleString('tr-TR', { style: 'currency', currency: record.currency })}
        </span>
      ),
    },
    {
      title: 'Dönem',
      key: 'period',
      render: (_: unknown, record: FirmFinancial) =>
        record.periodStart && record.periodEnd
          ? `${dayjs(record.periodStart).format('DD.MM.YYYY')} - ${dayjs(record.periodEnd).format('DD.MM.YYYY')}`
          : '-',
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: unknown, record: FirmFinancial) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingFinancial(record);
              financialForm.setFieldsValue({
                ...record,
                period: record.periodStart && record.periodEnd
                  ? [dayjs(record.periodStart), dayjs(record.periodEnd)]
                  : undefined,
              });
              setIsFinancialModalOpen(true);
            }}
          />
          <Popconfirm
            title="İşlemi silmek istediğinize emin misiniz?"
            onConfirm={() => deleteFinancialMutation.mutate(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      {financialSummary && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Toplam Gelir"
                value={financialSummary.totalRevenue}
                precision={2}
                suffix="TL"
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Yıllık Ücretler"
                value={financialSummary.totalAnnualFees}
                precision={2}
                suffix="TL"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Ek Satışlar"
                value={financialSummary.totalAddons}
                precision={2}
                suffix="TL"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="İndirimler"
                value={financialSummary.totalDiscounts}
                precision={2}
                suffix="TL"
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
        </Row>
      )}
      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingFinancial(null);
            financialForm.resetFields();
            financialForm.setFieldsValue({ currency: 'TRY' });
            setIsFinancialModalOpen(true);
          }}
        >
          Yeni İşlem
        </Button>
      </div>
      <Table columns={financialColumns} dataSource={financials} rowKey="id" />

      <Modal
        title={editingFinancial ? 'İşlem Düzenle' : 'Yeni İşlem'}
        open={isFinancialModalOpen}
        onCancel={() => {
          setIsFinancialModalOpen(false);
          setEditingFinancial(null);
          financialForm.resetFields();
        }}
        footer={null}
      >
        <Form form={financialForm} layout="vertical" onFinish={handleFinancialSubmit}>
          <Form.Item
            name="transactionType"
            label="İşlem Tipi"
            rules={[{ required: true, message: 'İşlem tipi gerekli' }]}
          >
            <Select>
              <Select.Option value="annual_fee">Yıllık Ücret</Select.Option>
              <Select.Option value="addon">Ek Satış</Select.Option>
              <Select.Option value="discount">İndirim</Select.Option>
              <Select.Option value="payment">Ödeme</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Açıklama"
            rules={[{ required: true, message: 'Açıklama gerekli' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="amount"
            label="Tutar"
            rules={[{ required: true, message: 'Tutar gerekli' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="currency" label="Para Birimi">
            <Select>
              <Select.Option value="TRY">TRY</Select.Option>
              <Select.Option value="USD">USD</Select.Option>
              <Select.Option value="EUR">EUR</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="period" label="Dönem">
            <DatePicker.RangePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="notes" label="Notlar">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={addFinancialMutation.isPending || updateFinancialMutation.isPending}>
                {editingFinancial ? 'Güncelle' : 'Ekle'}
              </Button>
              <Button onClick={() => setIsFinancialModalOpen(false)}>İptal</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
